<template>
  <PhaseSection
    :title="$t('common.rev_state.plan')"
    :description="$t('rev_cycle.relate.description')"
    :isEditMode="isEditMode"
    :hasNextButton="hasNextButton"
    :hasBackButton="hasBackButton"
    @next="$emit('next')"
    @back="$emit('back')"
  >
    <MediaContainer
      data-private
      id="relatePhoto"
      slot="left"
      class="mt-6"
      :images="media"
      :readOnly="!isEditMode || isSaving"
      @change="handleMediaChange"
    />
    <TranslationLoading slot="right" v-if="isTranslating" />
    <div slot="right" v-else>
      <p class="d-flex justify-end caption blue-grey--text mb-6">
        * {{ $t('common.required_fields') }}
      </p>
      <TextQuestion
        v-model="problem"
        data-private
        rows="4"
        tid="improvementIdea-textarea-problem"
        tourId="relate-textField-problem"
        :maxLength="$constants.improvementIdea.whatIsTheProblemMaxLength"
        :question="`${$t('rev_cycle.relate.what_is_the_problem')}*`"
        :placeholder="$t('rev_cycle.relate.problem_textarea_placeholder')"
        :isEditMode="isEditMode"
        :error="markRequiredFields && !problem"
        @focusOut="trackEvent('problem')"
      />

      <TextQuestion
        v-model="idea"
        data-private
        rows="6"
        tid="improvementIdea-textarea-idea"
        tourId="relate-textField-idea"
        :class="{ 'mt-0': isEditMode, 'mt-6': !isEditMode }"
        :maxLength="$constants.improvementIdea.whatIsTheIdeaMaxLength"
        :question="`${$t('rev_cycle.relate.what_is_the_idea')}*`"
        :placeholder="$t('rev_cycle.relate.idea_textarea_placeholder')"
        :isEditMode="isEditMode"
        :error="markRequiredFields && !idea"
        @focusOut="trackEvent('idea')"
      />

      <TextQuestion
        v-model="expectedResult"
        data-private
        rows="6"
        tid="improvementIdea-textarea-expectedResult"
        tourId="relate-textField-expectedResult"
        :class="{ 'mt-0': isEditMode, 'mt-6': !isEditMode }"
        :maxLength="$constants.improvementIdea.expectedResultMaxLength"
        :question="`${$t('rev_cycle.relate.what_is_the_expected_result')}*`"
        :placeholder="$t('rev_cycle.relate.expect_textarea_placeholder')"
        :isEditMode="isEditMode"
        :error="markRequiredFields && !expectedResult"
        @focusOut="trackEvent('expectedResult')"
      />
      <div :class="{ 'mt-0': isEditMode, 'mt-6': !isEditMode }">
        <CategoriesSelector
          v-if="isEditMode"
          v-model="selectedCategories"
          data-private
          isRequired
          data-testid="improvement-idea-relate-categories"
          :siteIds="[siteId]"
          :error="markRequiredFields && !selectedCategories.length"
        />
        <ChipsList
          v-else
          translationKey="common.categories"
          :items="selectedCategories"
          :title="`${$t('common.category_other')}*`"
        />
      </div>
    </div>
  </PhaseSection>
</template>

<script>
import { mapMutations } from 'vuex'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import PhaseSection from '@/components/Rev/PhaseSection/PhaseSection.vue'
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import TextQuestion from '@/components/Rev/TextQuestion.vue'
import CategoriesSelector from '@/components/CategoriesSelector.vue'
import TranslationLoading from '@/components/Rev/TranslationLoading.vue'
import ChipsList from '@/components/ChipsList.vue'

export default {
  name: 'Relate',

  mixins: [trackerMixin],

  components: {
    PhaseSection,
    TextQuestion,
    CategoriesSelector,
    MediaContainer,
    TranslationLoading,
    ChipsList,
  },

  props: {
    organizationId: {
      type: String,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      required: true,
    },
    rev: {
      type: Object,
      required: true,
    },
    isSaving: Boolean,
    isTranslating: Boolean,
    hasNextButton: Boolean,
    hasBackButton: Boolean,
    markRequiredFields: Boolean,
  },

  computed: {
    selectedCategories: {
      get() {
        return this.rev?.categories ?? []
      },

      set(categories) {
        this.trackUpdate(this.rev, 'changeCategories')

        return this.updateCategories(categories)
      },
    },

    problem: {
      get() {
        return this.rev.relate.problem
      },
      set(problem) {
        this.$emit('revChanged')
        this.updateProblem(problem)
      },
    },

    idea: {
      get() {
        return this.rev.relate.idea
      },
      set(idea) {
        this.$emit('revChanged')
        this.updateIdea(idea)
      },
    },

    expectedResult: {
      get() {
        return this.rev.relate.expectedResult
      },
      set(expectedResult) {
        this.$emit('revChanged')
        this.updateExpectedResult(expectedResult)
      },
    },

    media() {
      return this.rev?.relate?.media ?? []
    },

    siteId() {
      return this.rev.location?.site?._id || this.rev.location?.site
    },
  },

  methods: {
    ...mapMutations({
      updateProblem: 'improvementIdea/updateProblem',
      updateIdea: 'improvementIdea/updateIdea',
      updateExpectedResult: 'improvementIdea/updateExpectedResult',
      updateCategories: 'improvementIdea/updateCategories',
      setRelateMedia: 'improvementIdea/setRelateMedia',
    }),

    trackEvent(changedField) {
      this.trackUpdate(this.rev, changedField)
    },

    handleMediaChange(media) {
      this.setRelateMedia(media)
      this.trackEvent('relatePhotos')
      this.$emit('revChanged')
    },
  },
}
</script>
